import axios from 'axios';
import { API_BASE_URL } from './api';

const API_URL = API_BASE_URL;

export interface Activity {
  _id?: string;
  name: string;
  description: string;
  owner: string;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Blocked';
}

export interface Deliverable {
  _id?: string;
  name: string;
  description: string;
  dueDate: Date;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Delayed';
}

export interface Milestone {
  _id?: string;
  name: string;
  date: Date;
  achieved: boolean;
}

export interface TimelinePhase {
  _id?: string;
  phaseNumber: number;
  phaseName: string;
  weekRange: string;
  startDate: Date;
  endDate: Date;
  description: string;
  budget: number;
  activities: Activity[];
  deliverables: Deliverable[];
  milestones: Milestone[];
}

export interface DataInventoryTimeline {
  _id: string;
  projectName: string;
  projectStartDate: Date;
  projectEndDate: Date;
  totalDuration: number;
  totalBudget: number;
  status: 'Planning' | 'In Progress' | 'Completed' | 'On Hold';
  phases: TimelinePhase[];
  createdBy: string;
  updatedBy?: string;
  version: string;
  createdAt?: Date;
  updatedAt?: Date;
}

class DataInventoryTimelineService {
  async getTimeline(): Promise<DataInventoryTimeline> {
    // Add cache-busting parameter
    const timestamp = new Date().getTime();
    const response = await axios.get(`${API_URL}/data-inventory-timeline?_t=${timestamp}`);
    return response.data.data;
  }

  async updateTimeline(id: string, data: Partial<DataInventoryTimeline>): Promise<DataInventoryTimeline> {
    const response = await axios.put(`${API_URL}/data-inventory-timeline/${id}`, data);
    return response.data.data;
  }

  async updateStartDate(id: string, startDate: Date, updatedBy: string): Promise<DataInventoryTimeline> {
    const response = await axios.put(`${API_URL}/data-inventory-timeline/${id}/start-date`, {
      startDate,
      updatedBy
    });
    return response.data.data;
  }

  async updatePhase(timelineId: string, phaseId: string, data: Partial<TimelinePhase>): Promise<DataInventoryTimeline> {
    const response = await axios.put(`${API_URL}/data-inventory-timeline/${timelineId}/phases/${phaseId}`, data);
    return response.data.data;
  }

  async updateActivity(timelineId: string, phaseId: string, activityId: string, data: Partial<Activity>): Promise<DataInventoryTimeline> {
    const response = await axios.put(`${API_URL}/data-inventory-timeline/${timelineId}/phases/${phaseId}/activities/${activityId}`, {
      ...data,
      updatedBy: 'User'
    });
    return response.data.data;
  }

  async updateDeliverable(timelineId: string, phaseId: string, deliverableId: string, data: Partial<Deliverable>): Promise<DataInventoryTimeline> {
    const response = await axios.put(`${API_URL}/data-inventory-timeline/${timelineId}/phases/${phaseId}/deliverables/${deliverableId}`, {
      ...data,
      updatedBy: 'User'
    });
    return response.data.data;
  }

  async updateMilestone(timelineId: string, phaseId: string, milestoneId: string, data: Partial<Milestone>): Promise<DataInventoryTimeline> {
    const response = await axios.put(`${API_URL}/data-inventory-timeline/${timelineId}/phases/${phaseId}/milestones/${milestoneId}`, {
      ...data,
      updatedBy: 'User'
    });
    return response.data.data;
  }
}

export default new DataInventoryTimelineService();
